// Receipt Types
export interface Receipt {
  id: number;
  order_id: number;
  invoice_id: number;
  receipt_number: string;
  payment_method: 'cash' | 'card' | 'others';
  total_amount: string; // DECIMAL(10,2) as string for precision
  amount_paid: string;  // DECIMAL(10,2) as string for precision
  change_given: string; // DECIMAL(10,2) as string for precision
  paid_at?: string | null;
  cashier_id?: number | null;
  is_printed: boolean;
  status: 'generated' | 'printed' | 'emailed' | 'voided';
  created_at: string;
  updated_at: string;
}
// API Response Types
export interface APIResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  meta: MetaData;
}

export interface MetaData {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// User Types
export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'admin' | 'manager' | 'counter';
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
    accessToken: string;
    user: User;
}

// Category Types
export interface Category {
  id: string;
  name: string;
  image_url: string;
  description?: string;
  color?: string;
  created_at: string;
  updated_at: string;
}


export interface Stock {
  id: string;
  name: string;
  description?: string;
  category_id?: number | null;
  unit: string;
  quantity: number;
  reserved_quantity: number;
  low_stock_threshold: number;
  cost_per_unit: number;
  image_url?: string;
  supplier: string;
  supplier_contact: string;
  last_restocked_at?: string | null;
  is_available: boolean;
  created_at: string;
  updated_at: string;
  category?: Category;
}

// Inventory transaction record returned by API
export interface Transaction {
  id?: number;
  stock_id: number;
  Stock?: Stock;
  User: { id?: number; name?: string };
  approved_at?: string | null;
  approved_by?: string | null;
  created_at: string;
  updated_at?: string;
  previous_quantity?: string;
  new_quantity?: string;
  quantity?: string;
  notes?: string;
  performed_by?: number | string | null;
  reference_type?: string;
  status?: string;
  total_cost?: number | null;
  transaction_date?: string;
  transaction_type?: string;
}

export interface InventorySummary {
  total: number;
  lowStock: number;
  outOfStock: number;
  inStock: number;
  totalValue: number;
}


// Table Types
export interface DiningTable {
  id: string;
  table_number: string;
  capacity: number;
  location?: string;
  status: 'available' | 'occupied';
  created_at: string;
  updated_at: string;
}

// Summary statistics for tables (total/available/occupied and occupancy rate as a formatted string)
export interface TableStats {
  total: number;
  available: number;
  occupied: number;
  occupancyRate: string; // e.g. "75%"
}

// Pagination shape returned by admin tables endpoint - support common key variants
export interface TablesPagination {
  // camelCase
  currentPage?: number;
  perPage?: number;
  totalPages?: number;
  // snake_case (some endpoints use this)
  current_page?: number;
  per_page?: number;
  total_pages?: number;
  // other common variants
  total?: number;
  totalTables?: number;
  [key: string]: any;
}

// Response from admin tables endpoint
export interface AdminTablesResponse {
  tables: DiningTable[];
  pagination: TablesPagination;
}

// Order Types
export interface Order {
  id: string;
  table_id?: string;
  waiter_id?: string;
  customer_name?: string;
  order_type: 'dine_in' | 'takeout' | 'delivery';
  status: 'confirmed' | 'preparing' | 'served' | 'completed' | 'cancelled';
  subtotal?: number;
  service_charge?: number;
  discount_amount?: number;
  price?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  served_at?: string;
  completed_at?: string;
  RestaurantTable?: DiningTable;
  user?: User;
  OrderItems?: OrderItem[];
  payments?: Payment[];
  Receipt?: Receipt;
  orderTotal?: number;
}
export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PREPARING = 'preparing',
  READY = 'ready',
  SERVED = 'served',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}
export interface OrderItem {
  id: string;
  order_id: string;
  stock_id: string;
  quantity: number;
  unit_price?: number;
  price?: number;
  status: OrderStatus;
  created_at: string;
  updated_at: string;
  Stock?: Stock;
  notes?: string; // Alternative field name for special instructions
}

export interface CreateOrderRequest {
  table_id?: string;
  order_type: 'walk_in' | 'scheduled' | 'emergency';
  items: CreateOrderItem[];
  notes?: string;
}

export interface UpdateOrderRequest {
  order_id: string;
  items?: CreateOrderItem[];
  notes?: string;
}
export interface CreateOrderItem {
  stock_id: string;
  quantity: number;
}

export interface UpdateOrderStatusRequest {
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'served' | 'completed' | 'cancelled';
  notes?: string;
}

// Payment Types
export interface Payment {
  id: string;
  order_id: string;
  invoice_id: string;
  payment_method: 'cash' | 'card' | 'insurance' | 'others';
  amount: number;
  reference_number?: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  processed_by?: string;
  processed_at?: string;
  created_at: string;
  failure_reason?: string;
  note?: string;
  processed_by_user?: User;
}

export interface ProcessPaymentRequest {
  payment_method: 'cash' | 'card' | 'insurance' | 'others';
  amount: number;
  reference_number?: string;
}

export interface PaymentSummary {
  order_id: string;
  total_amount: number;
  total_paid: number;
  pending_amount: number;
  remaining_amount: number;
  is_fully_paid: boolean;
  payment_count: number;
}

export interface CartItem {
  Stock: Stock;
  quantity: number;
}

export interface Cart {
  items: CartItem[];
  subtotal: number;
  service_charge: number;
  total_amount: number;
}

// Dashboard Types
export interface DashboardStats {
  activeOrders: number;
  detailed: any;
  occupiedTables: number;
  todaysOrders: {
    comparisonText: string;
    count: number;
    percentageChange: number;
  }
  todaysRevenue: {
    comparisonText: string;
    amount: number;
    percentageChange: number;
    formattedAmount: string;
  }
}

export interface SalesReportItem {
  date: string;
  order_count: number;
  revenue: number;
}

// Sales Report Response Types
export interface SalesReportDateRange {
  start: string; // ISO date string
  end: string;   // ISO date string
}
export interface SalesReport {
  period: string;
  dateRange: SalesReportDateRange;
  data: Order[]; // Array of order data with Receipt information
}

// Analytics / Reports types
export interface AnalyticsAmountMetric {
  amount: number;
  formattedAmount?: string;
  label?: string;
  period?: string;
  comparisonText?: string;
  percentageChange?: number;
}

export interface AnalyticsCountMetric {
  count: number;
  label?: string;
  period?: string;
  comparisonText?: string;
}

export interface AnalyticsPercentageMetric {
  percentage: number;
  formattedPercentage?: string;
  label?: string;
  period?: string;
  isPositive?: boolean;
}

export interface AnalyticsResponse {
  // common named metrics (matches the sample response)
  totalRevenue?: AnalyticsAmountMetric;
  totalExpenses?: AnalyticsAmountMetric;
  netProfit?: AnalyticsAmountMetric & { isPositive?: boolean };
  totalOrders?: AnalyticsCountMetric;
  averageOrder?: AnalyticsAmountMetric;
  growthRate?: AnalyticsPercentageMetric;
  data: Order[];
  period: string;
  dateRange: SalesReportDateRange;
  selectedPeriod?: string;

  // simple numeric stats
  completedOrders?: number;
  cancelledOrders?: number;
}

// Report filter parameters interface
export interface ReportFilterParams {
  startDate?: string;
  endDate?: string;
  orderType?: string;
  status?: string;
  minAmount?: number | null;
  maxAmount?: number | null;
  searchTerm?: string;
  page?: number;
  limit?: number;
}

export interface OrdersReportItem {
  status: string;
  count: number;
  avg_amount: number;
}

// Kitchen Types
export interface KitchenOrder {
  id: string;
  order_number: string;
  table_id?: string;
  table_number?: string;
  order_type: string;
  status: string;
  customer_name?: string;
  created_at: string;
  items?: OrderItem[];
}

// Table Status Types
export interface TableStatus {
  total_tables: number;
  occupied_tables: number;
  available_tables: number;
  occupancy_rate: number;
  by_location: LocationStats[];
}

export interface LocationStats {
  location: string;
  total_tables: number;
  occupied_tables: number;
  available_tables: number;
  occupancy_rate: number;
}

// Filter and Query Types
export interface OrderFilters {
  status?: string;
  order_type?: string;
  page?: number;
  per_page?: number;
}

export interface StockFilters {
  category_id?: string;
  available?: boolean;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface TableFilters {
  location?: string;
  occupied_only?: boolean;
  available_only?: boolean;
}

// Expense Types
export interface Expense {
  id: string;
  title: string;
  description: string;
  amount: number;
  category: string;
  status: 'pending' | 'approved' | 'rejected';
  expense_date: string;
  receipt_url?: string;
  notes?: string;
  submitted_by: string;
  Submitter?: User;
  approved_by?: string;
  Approver?: User;
  approved_at?: string;
  rejected_at?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateExpenseRequest {
  description: string;
  amount: number;
  category: string;
  expense_date: string;
  receipt_url?: string;
  notes?: string;
}

export interface UpdateExpenseRequest {
  description?: string;
  amount?: number;
  category?: string;
  expense_date?: string;
  receipt_url?: string;
  notes?: string;
}

export interface ApproveExpenseRequest {
  notes?: string;
}

export interface RejectExpenseRequest {
  rejection_reason: string;
}

export interface ExpenseFilters {
  status?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface ExpenseSummary {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  totalAmount: number;
  approvedAmount: number;
  pendingAmount: number;
}

